import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { clsx } from "clsx";
import {
  Printer,
  FileSignature,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
  Clock,
  Users,
  AlertCircle,
  CheckCircle2,
  XCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { DatePicker } from "@/components/ui/DatePicker";
import { PageLoader } from "@/components/ui/Spinner";
import { useQuery } from "@tanstack/react-query";
import apiClient from "@/api/client";
import type { DesignDocumentStatus, ClusterSigningMode, ManagedFileBrief } from "../types";
import { STATUS_LABELS } from "../types";
import { useDesignerActivity } from "../api/designerActivity";
import type { DesignerActivityEntry } from "../api/designerActivity";

// ── Revision log types (same as DocActivityPage) ─────────────────────────────

interface RevisionLogEntry {
  id: string;
  revision_number: number;
  status: DesignDocumentStatus;
  reason: string;
  document_title: string;
  cluster_id: string;
  cluster_name: string;
  cluster_signing_mode: ClusterSigningMode;
  project_id: string;
  project_name: string;
  /** Signer display name — contractor company OR employee signer full name. */
  contractor_name: string | null;
  /** Signer's UUID — contractor.id OR employee_signer.user_id. */
  signer_id: string | null;
  signer_kind: "contractor" | "employee" | "unknown";
  created_by_name: string | null;
  created_at: string;
  signed_at: string | null;
  acknowledged_at: string | null;
  original_file: ManagedFileBrief | null;
  signed_file: ManagedFileBrief | null;
}

function useRevisionLog(filters: {
  status?: string;
  date_from?: string;
  date_to?: string;
}) {
  const params: Record<string, string> = {};
  if (filters.status) params.status = filters.status;
  if (filters.date_from) params.date_from = filters.date_from;
  if (filters.date_to) params.date_to = filters.date_to;

  return useQuery({
    queryKey: ["doc-revision-log", filters],
    queryFn: () =>
      apiClient
        .get<{ data: { results: RevisionLogEntry[] }; meta: { count: number } }>(
          "/project-docs/revision-log/",
          { params },
        )
        .then((r) => r.data),
    staleTime: 30_000,
  });
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_BADGE_COLOR: Record<string, "yellow" | "green" | "red" | "gray"> = {
  pending_signature: "yellow",
  signed: "green",
  rejected: "red",
};

function fmt(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function fmtTurnaround(hours: number | null) {
  if (hours === null) return "—";
  if (hours < 24) return `${hours} godz.`;
  return `${Math.round(hours / 24)} dni`;
}

type DesignerSortKey = keyof Pick<
  DesignerActivityEntry,
  "contractor_name" | "cluster_count" | "total_revisions" | "pending_count" | "signed_count" | "rejected_count" | "avg_turnaround_hours" | "last_activity_at"
>;

type RevSortKey = "document_title" | "cluster_name" | "contractor_name" | "created_by_name" | "status" | "created_at" | "signed_at";

function SortIcon({ active, dir }: { active: boolean; dir: "asc" | "desc" }) {
  if (!active) return <ChevronsUpDown className="h-3 w-3 text-gray-300 dark:text-gray-600" />;
  return dir === "asc"
    ? <ChevronUp className="h-3 w-3 text-gray-500 dark:text-gray-400" />
    : <ChevronDown className="h-3 w-3 text-gray-500 dark:text-gray-400" />;
}

function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: "blue" | "yellow" | "green" | "red";
}) {
  const ring = {
    blue: "bg-blue-50 dark:bg-blue-950/20 text-blue-600 dark:text-blue-400",
    yellow: "bg-amber-50 dark:bg-amber-950/20 text-amber-600 dark:text-amber-400",
    green: "bg-green-50 dark:bg-green-950/20 text-green-600 dark:text-green-400",
    red: "bg-red-50 dark:bg-red-950/20 text-red-600 dark:text-red-400",
  }[color];

  return (
    <div className="flex items-center gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 min-w-36">
      <div className={clsx("p-2 rounded-lg", ring)}>{icon}</div>
      <div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
        <p className="text-lg font-bold text-gray-900 dark:text-gray-100">{value}</p>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export function DesignerActivityPage() {
  const navigate = useNavigate();

  // Shared date range
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Designer table state
  const [selectedContractor, setSelectedContractor] = useState<{ id: string; name: string } | null>(null);
  const [dSortKey, setDSortKey] = useState<DesignerSortKey>("last_activity_at");
  const [dSortDir, setDSortDir] = useState<"asc" | "desc">("desc");

  // Revision table state
  const [statusFilter, setStatusFilter] = useState("");
  const [rSortKey, setRSortKey] = useState<RevSortKey>("created_at");
  const [rSortDir, setRSortDir] = useState<"asc" | "desc">("desc");

  const { data: activityData, isLoading: activityLoading } = useDesignerActivity({
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const { data: revisionData, isLoading: revisionLoading } = useRevisionLog({
    status: statusFilter || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  });

  const designerEntries = activityData?.data.results ?? [];
  const allRevisions = revisionData?.data.results ?? [];

  // Summary stats
  const totalPending = designerEntries.reduce((s, d) => s + d.pending_count, 0);
  const totalSigned = designerEntries.reduce((s, d) => s + d.signed_count, 0);
  const totalRejected = designerEntries.reduce((s, d) => s + d.rejected_count, 0);

  // Sort designer rows
  function handleDSort(key: DesignerSortKey) {
    if (dSortKey === key) setDSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setDSortKey(key); setDSortDir("asc"); }
  }

  const sortedDesigners = [...designerEntries].sort((a, b) => {
    const av = a[dSortKey] ?? "";
    const bv = b[dSortKey] ?? "";
    const cmp = String(av).localeCompare(String(bv), "pl");
    return dSortDir === "asc" ? cmp : -cmp;
  });

  // Filter and sort revisions. We key off the signer's UUID (works for both
  // external contractors AND employee signers, and avoids name-collision bugs
  // when two signers happen to share a display name).
  const filteredRevisions = allRevisions.filter(
    (e) => !selectedContractor || e.signer_id === selectedContractor.id,
  );

  function handleRSort(key: RevSortKey) {
    if (rSortKey === key) setRSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else { setRSortKey(key); setRSortDir("asc"); }
  }

  const sortedRevisions = [...filteredRevisions].sort((a, b) => {
    const av = a[rSortKey] ?? "";
    const bv = b[rSortKey] ?? "";
    const cmp = String(av).localeCompare(String(bv), "pl");
    return rSortDir === "asc" ? cmp : -cmp;
  });

  const hasFilters = !!(dateFrom || dateTo);

  function clearFilters() {
    setDateFrom("");
    setDateTo("");
    setStatusFilter("");
    setSelectedContractor(null);
  }

  if (activityLoading || revisionLoading) return <PageLoader />;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
          Dziennik aktywności projektantów sprawdzających
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
          Aktywność projektantów sprawdzających w podziale na firmy
        </p>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-end gap-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3">
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Od</label>
          <DatePicker
            value={dateFrom}
            onChange={setDateFrom}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Do</label>
          <DatePicker
            value={dateTo}
            onChange={setDateTo}
          />
        </div>
        <div>
          <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Status rewizji</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="text-sm border border-gray-200 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            <option value="">Wszystkie</option>
            <option value="pending_signature">Oczekuje na sprawdzenie</option>
            <option value="signed">Sprawdzone</option>
            <option value="rejected">Odrzucone</option>
          </select>
        </div>
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 py-1 px-2"
          >
            Wyczyść filtry
          </button>
        )}
      </div>

      {/* Summary stats */}
      <div className="flex flex-wrap gap-3">
        <StatCard
          icon={<Users size={16} />}
          label="Projektantów"
          value={designerEntries.length}
          color="blue"
        />
        <StatCard
          icon={<AlertCircle size={16} />}
          label="Oczekujące"
          value={totalPending}
          color="yellow"
        />
        <StatCard
          icon={<CheckCircle2 size={16} />}
          label="Sprawdzone"
          value={totalSigned}
          color="green"
        />
        <StatCard
          icon={<XCircle size={16} />}
          label="Odrzucone"
          value={totalRejected}
          color="red"
        />
      </div>

      {/* Designer stats table */}
      <section>
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Podsumowanie per projektant
        </h2>
        {sortedDesigners.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
            Brak danych w wybranym zakresie dat.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs">
                <tr>
                  {(
                    [
                      ["contractor_name", "Projektant"],
                      ["cluster_count", "Klastry"],
                      ["total_revisions", "Rewizje"],
                      ["pending_count", "Oczekujące"],
                      ["signed_count", "Sprawdzone"],
                      ["rejected_count", "Odrzucone"],
                      ["avg_turnaround_hours", "Śr. czas"],
                      ["last_activity_at", "Ostatnia aktywność"],
                    ] as [DesignerSortKey, string][]
                  ).map(([key, label]) => (
                    <th
                      key={key}
                      className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100"
                      onClick={() => handleDSort(key)}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        <SortIcon active={dSortKey === key} dir={dSortDir} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortedDesigners.map((d) => {
                  const isSelected = selectedContractor?.id === d.contractor_id;
                  return (
                    <tr
                      key={d.contractor_id}
                      onClick={() =>
                        setSelectedContractor(
                          isSelected ? null : { id: d.contractor_id, name: d.contractor_name },
                        )
                      }
                      className={clsx(
                        "cursor-pointer transition-colors",
                        isSelected
                          ? "bg-blue-50 dark:bg-blue-950/20"
                          : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800",
                      )}
                    >
                      <td className="px-4 py-3 font-medium text-gray-900 dark:text-gray-100">
                        {d.contractor_name}
                        {d.kind === "employee" && (
                          <span className="ml-1.5 text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400">
                            (pracownik)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {d.cluster_count}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400">
                        {d.total_revisions}
                      </td>
                      <td className="px-4 py-3">
                        {d.pending_count > 0 ? (
                          <Badge color="yellow">{d.pending_count}</Badge>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {d.signed_count > 0 ? (
                          <Badge color="green">{d.signed_count}</Badge>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {d.rejected_count > 0 ? (
                          <Badge color="red">{d.rejected_count}</Badge>
                        ) : (
                          <span className="text-gray-400">0</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5 text-gray-400" />
                          {fmtTurnaround(d.avg_turnaround_hours)}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {fmtDate(d.last_activity_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Revision detail table */}
      <section>
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            Rewizje dokumentów
          </h2>
          {selectedContractor ? (
            <span className="flex items-center gap-1.5 text-xs text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-950/20 px-2 py-0.5 rounded-full">
              {selectedContractor.name}
              <button
                type="button"
                onClick={() => setSelectedContractor(null)}
                className="hover:text-blue-800 dark:hover:text-blue-200"
              >
                ×
              </button>
            </span>
          ) : (
            <span className="text-xs text-gray-400">wszystkie</span>
          )}
          {sortedRevisions.length > 0 && (
            <span className="text-xs text-gray-400 ml-auto">
              {sortedRevisions.length} rekordów
            </span>
          )}
        </div>

        {sortedRevisions.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-center py-8 text-gray-400 dark:text-gray-500 text-sm">
            Brak rewizji do wyświetlenia.
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-xs">
                <tr>
                  {(
                    [
                      ["document_title", "Dokument"],
                      ["cluster_name", "Klaster"],
                      ["contractor_name", "Podpisujący"],
                      ["created_by_name", "Przekazał"],
                      ["status", "Status"],
                      ["created_at", "Dodano"],
                      ["signed_at", "Sprawdzono"],
                    ] as [RevSortKey, string][]
                  ).map(([key, label]) => (
                    <th
                      key={key}
                      className="px-4 py-2 text-left font-medium cursor-pointer select-none hover:text-gray-900 dark:hover:text-gray-100"
                      onClick={() => handleRSort(key)}
                    >
                      <div className="flex items-center gap-1">
                        {label}
                        <SortIcon active={rSortKey === key} dir={rSortDir} />
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                {sortedRevisions.map((entry) => {
                  const isPaper = entry.cluster_signing_mode === "paper";
                  return (
                    <tr
                      key={entry.id}
                      className={clsx(
                        "cursor-pointer transition-colors",
                        isPaper
                          ? "bg-amber-50/40 dark:bg-amber-950/10 hover:bg-amber-50 dark:hover:bg-amber-950/20"
                          : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800",
                      )}
                      onClick={() =>
                        navigate(`/projects/${entry.project_id}/documentation/${entry.cluster_id}`)
                      }
                    >
                      <td className="px-4 py-3">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {entry.document_title}
                        </p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">
                          Wersja {entry.revision_number}
                          {entry.reason ? ` · ${entry.reason}` : ""}
                        </p>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5">
                          {isPaper ? (
                            <Printer className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                          ) : (
                            <FileSignature className="h-3.5 w-3.5 text-blue-400 shrink-0" />
                          )}
                          <div>
                            <p className="text-sm text-gray-700 dark:text-gray-300">
                              {entry.cluster_name}
                            </p>
                            <p className="text-xs text-gray-400 dark:text-gray-500">
                              {entry.project_name}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.contractor_name ?? "—"}
                        {entry.signer_kind === "employee" && (
                          <span className="ml-1 text-[10px] uppercase tracking-wide text-blue-600 dark:text-blue-400">
                            (pracownik)
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-400">
                        {entry.created_by_name ?? "—"}
                      </td>
                      <td className="px-4 py-3">
                        <Badge color={STATUS_BADGE_COLOR[entry.status] ?? "gray"}>
                          {STATUS_LABELS[entry.status]}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {fmt(entry.created_at)}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                        {fmt(entry.signed_at)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
